import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { DisplayChangeStatusModel, DisplayModel, GroupModel, PlayerSelectListItemModel, PlayerTypeListItemModel, ScreenPurchaseLogModel, SelectListItemModel } from "src/app/shared/models";
import { SubscriptionSummaryModel, UpdateSubscriptionModel } from "src/models";
import { BaseService, DisplayListService, DisplayService, FolderService, GroupService, PageContentService, ScreenPurchaseLogService, GTMAnalyticsService } from "src/app/shared/services";
import { ScreenActivationTracker } from 'src/app/display/services';
import { PlayerTypeService } from "src/app/shared/services/player-type.service";
import { AppUtils, Constants } from "src/helpers";
import { PlanAccessService, SubscriptionService, UserService } from "src/services";
import { Subscription } from "rxjs";
import { SharedModule } from '../../../../shared/shared.module';
import { ContentTextViewComponent } from "../../content-text-view/content-text-view.component";

@Component({
    standalone: true,
    imports: [SharedModule, ContentTextViewComponent],
    selector: 'app-display-upsert',
    templateUrl: './display-upsert.component.html',
    styleUrls: ['./display-upsert.component.scss']
})

export class DisplayUpsertComponent implements OnInit {
    @Output() closeDrawer: EventEmitter<{ selectedDisplay: SelectListItemModel, updateValue: boolean }> = new EventEmitter<{ selectedDisplay: SelectListItemModel, updateValue: boolean }>();
    @Input() displayId: any;
    @Input() groupId: number;
    @Input() addFromPlaylist: boolean = false;

    isLoading = false;
    model: DisplayModel;
    playerListModel: Array<PlayerSelectListItemModel>;
    playerTypeList: Array<PlayerTypeListItemModel>;
    groups: Array<GroupModel>;
    folders: Array<SelectListItemModel>;
    subscriptionPlans: Array<SelectListItemModel>;
    selectedGroupId: number;
    isConfirmVisible: boolean;
    selectOrientation = false;
    displayCanBeAdded: boolean;
    priceHidden: any;
    confirmAddDisplay: boolean;
    addedDisplayId = 0;

    selectedGroupOwnerEmail: string;
    totalDisplayCount: number;
    isDisplayCountLoaded: boolean;
    isSubscriptionDetailLoaded: boolean;
    subscriptionSummaryModel: SubscriptionSummaryModel;
    isIAmOwner: boolean;
    hasSubscriptionExpired: boolean;
    selectedPlayerType: any;
    playerTypeInfoMessage: any;
    isDisplayListLoaded = false;
    displayLists = new Array<SelectListItemModel>();
    subscriptions = new Array<Subscription>();
    totalDisplayCountToSubscribe = 0;

    constructor(private baseService: BaseService,
        private appUtils: AppUtils,
        private service: DisplayService,
        private groupService: GroupService,
        private folderService: FolderService,
        private playerTypeService: PlayerTypeService,
        private subscriptionService: SubscriptionService,
        private pageContentService: PageContentService,
        private sanitizer: DomSanitizer,
        private displayListService: DisplayListService,
        private userService: UserService,
        private screenPurchaseLogService: ScreenPurchaseLogService,
        private gtmAnalyticsService: GTMAnalyticsService,
        private screenActivationTracker: ScreenActivationTracker,
        public planAccess: PlanAccessService
    ) {
        this.playerListModel = new Array<PlayerSelectListItemModel>();
        this.playerTypeList = new Array<PlayerTypeListItemModel>();
        this.folders = new Array<SelectListItemModel>();
        this.model = new DisplayModel();
        this.groups = new Array<GroupModel>();
        this.subscriptionPlans = new Array<SelectListItemModel>();

    }

    async ngOnInit() {
        await this.getGroups();
        this.getPlayerTypeList();
        this.getFolders();
        this.loadSubscriptionDetail();
        this.loadTotalDisplayCount();
        this.getDisplayLists();
        // Opened as a drawer (no route change) so the usual NavigationEnd refresh doesn't fire —
        // pull fresh usage so the screen-limit guard reflects the current count.
        this.planAccess.refresh().subscribe();
    }

    onVisibleChange(): void {
        this.selectOrientation = !this.selectOrientation;
    }

    toUpperCase(event: any): void {
        this.model.displayCode = event.target.value.toUpperCase();
    }

    getPlayerTypeList(): void {
        const requestSubscription =
            this.playerTypeService.getList()
                .subscribe({
                    next: (response: Array<PlayerTypeListItemModel>) => {
                        this.playerTypeList = new Array<PlayerTypeListItemModel>();
                        this.playerTypeList = response;
                        this.playerTypeList.forEach((x) => {
                            x.iconUrl = this.generateIconUrl(x.code);
                        });
                    },
                    error: (error) => {
                        this.baseService.processErrorResponse(error);
                    }
                });
        this.subscriptions.push(requestSubscription);
    }

    generateIconUrl(code: string): string {
        return `./assets/images/player-types/${code}.png`;
    }

    selectPlayerType(item: any): void {
        this.model.playerTypeId = item.id;
        this.model.playerType = item.code;
        this.getInfoMessage(item.code);
    }

    getInfoMessage(code: string): void {
        this.isLoading = true;
        const infoMessageType = `ADD_DISPLAY_PLAYER_${code}`;
        this.pageContentService.getByType(infoMessageType)
            .subscribe({
                next: (response: any) => {
                    if (response) {
                        let text = response?.content;
                        this.playerTypeInfoMessage = this.sanitizer.bypassSecurityTrustHtml(text);
                    }
                    else {
                        this.playerTypeInfoMessage = null;
                    }
                    this.isLoading = false;
                },
                error: (error) => {
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    selectRotationDegree(degree: number): void {
        this.model.rotationDegree = degree;
    }

    loadTotalDisplayCount() {
        let userId = null;

        const selectedGroup = this.groups.find(x => x.id == this.model.groupId);
        if (AppUtils.isNullOrUndefined(selectedGroup)) {
            this.priceHidden = false;
        } else {
            this.selectedGroupOwnerEmail = selectedGroup.ownerEmail;
            this.priceHidden = selectedGroup.hidePricing && !selectedGroup.isGroupOwner && `${selectedGroup.ownerId}` !== this.appUtils.getUserId();
        }


        if (this.model.groupId == -1) {
            userId = this.appUtils.getUserId();
        } else {
            if (this.model.groupId == null) {
                return;
            }

            const groupSummary = this.groups.find(x => x.id == this.model.groupId);
            if (groupSummary) {
                localStorage.setItem(Constants.varGroupSummary, JSON.stringify(groupSummary));
                userId = groupSummary.ownerId;
            }
        }

        const requestSubscription = this.service.getDisplayCountByUserOwnership(userId)
            .subscribe({
                next: (response: number) => {

                    this.totalDisplayCount = response;
                    this.isDisplayCountLoaded = true;
                    this.updateDisplayCanBeAddedStatus();
                },
                error: (error) => {
                    this.baseService.processErrorResponse(error);
                }
            })

        this.subscriptions.push(requestSubscription);
    }

    updateDisplayCanBeAddedStatus() {
        if (!this.isSubscriptionDetailLoaded || !this.isDisplayCountLoaded) {
            return;
        }
        if (this.subscriptionSummaryModel.status !== 'InTrial') {
            this.displayCanBeAdded = this.isDisplayCountLoaded
                && (this.subscriptionSummaryModel.planQuantity + this.subscriptionSummaryModel.planFreeQuantity)
                > this.totalDisplayCount;
        } else {
            this.displayCanBeAdded = true;
        }
    }

    loadSubscriptionDetail() {

        this.displayCanBeAdded = true;

        if (this.model.groupId == null) {
            return;
        }

        let userId = null;

        if (this.model.groupId == -1) {
            userId = this.appUtils.getUserId();
        } else {
            const groupSummary = this.groups.find(x => x.id == this.model.groupId);
            if (groupSummary) {
                localStorage.setItem(Constants.varGroupSummary, JSON.stringify(groupSummary));
                userId = groupSummary.ownerId;
                this.isIAmOwner = !!groupSummary.isGroupOwner || userId === this.appUtils.getUserId();
            } else {
                this.isIAmOwner = undefined;
                userId = this.appUtils.getUserId();
            }
        }

        let requestSubscription = new Subscription();

        if (userId === null) {
            requestSubscription = this.subscriptionService.getDetailByGroupId(this.model.groupId)
                .subscribe({
                    next: (data: SubscriptionSummaryModel) => {
                        this.subscriptionSummaryModel = data;
                        this.isSubscriptionDetailLoaded = true;
                        if (this.subscriptionSummaryModel.status === 'Cancelled') {
                            this.hasSubscriptionExpired = true;
                        } else {
                            this.hasSubscriptionExpired = false;
                        }
                        this.updateDisplayCanBeAddedStatus();
                        this.isLoading = false;
                    },
                    error: (error: any) => {
                        this.isLoading = false;
                        this.baseService.processErrorResponse(error);
                    }
                });
        } else {
            requestSubscription = this.subscriptionService.getDetailByUserId(userId)
                .subscribe({
                    next: (data: SubscriptionSummaryModel) => {
                        this.subscriptionSummaryModel = data;
                        this.isSubscriptionDetailLoaded = true;
                        if (this.subscriptionSummaryModel.status === 'Cancelled') {
                            this.hasSubscriptionExpired = true;
                        } else {
                            this.hasSubscriptionExpired = false;
                        }
                        this.updateDisplayCanBeAddedStatus();
                        this.isLoading = false;
                    },
                    error: (error: any) => {
                        this.isLoading = false;
                        this.baseService.processErrorResponse(error);
                    }
                });
        }
        this.isLoading = true;

        this.subscriptions.push(requestSubscription);
    }

    getGroups(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.isLoading = true;
            const requestSubscription = this.groupService.getList()
                .subscribe({
                    next: (response: Array<GroupModel>) => {
                        this.groups = new Array<GroupModel>();
                        this.groups = response;

                        this.subscriptionPlans = new Array<SelectListItemModel>();
                        let subscriptionPlan = new SelectListItemModel();
                        subscriptionPlan.keyInt = -1;
                        subscriptionPlan.value = `My Subscription (${this.appUtils.getUserName()})`;
                        this.subscriptionPlans.push(subscriptionPlan);

                        this.groups.forEach((group => {
                            subscriptionPlan = new SelectListItemModel();
                            subscriptionPlan.keyInt = group.id;
                            subscriptionPlan.value = `${group.name} (${group.ownerName})`;
                            this.subscriptionPlans.push(subscriptionPlan);
                        }));

                        if (this.groupId != undefined && this.groupId > 0) {
                            this.model.groupId = this.groupId
                            this.selectedGroupId = this.groupId;
                        }
                        else {
                            this.selectedGroupId = -1;
                            this.model.groupId = -1;
                        }

                        this.getFolders();
                        this.getDisplayLists();
                        this.isLoading = false;
                        resolve(true);
                    },
                    error: (error: any) => {
                        this.isLoading = false;
                        this.baseService.processErrorResponse(error);
                        reject(false);
                    }
                });
            this.subscriptions.push(requestSubscription);
        });
    }

    onGroupChange(): void {
        this.model.folderId = null;
        this.model.displayListId = null;
        this.model.broadcastId = null;
        this.getFolders();
        this.getDisplayLists();
    }

    confirmGroupChange(groupId: any) {
        if (this.model.displayListId != null || this.model.displayListId > 0) {
            this.isConfirmVisible = true;
        }
        else {
            this.model.groupId = groupId;
            this.onGroupChange();
        }
    }

    getFolders(): void {
        const requestSubscription = this.folderService.getList(this.model.groupId, null)
            .subscribe({
                next: (response: Array<SelectListItemModel>) => {
                    this.folders = response;
                },
                error: (error: any) => {
                    this.baseService.processErrorResponse(error);
                }
            })
        this.subscriptions.push(requestSubscription);
    }

    getDisplayLists() {
        this.isLoading = true;
        this.isDisplayListLoaded = false;
        this.displayListService.getSelectListItems(this.model.groupId).subscribe({
            next: (response: any) => {
                this.displayLists = response;
                this.isLoading = false;
                this.isDisplayListLoaded = true;
            },
            error: (error: any) => {
                this.isLoading = false;
                this.isDisplayListLoaded = true;
                this.baseService.processErrorResponse(error);
            }
        })
    }

    submit(): void {
        // Plan enforcement: keep screen-limit messaging consistent with the Screen module.
        // On a hard plan limit (e.g. Free at max_displays) show the upgrade modal and stop —
        // not the pay-per-screen billing confirmation below. Unlimited (pay-per-screen) plans
        // are never "at limit", so they fall through to the existing billing flow unchanged.
        if (!this.planAccess.guardLimit('max_displays')) {
            return;
        }

        if (!this.displayCanBeAdded && !this.priceHidden) {
            this.confirmAddDisplay = true;
        }
        else {
            this.confirmAddDisplay = false;
            this.addDisplay();
        }
    }

    cancel(): void {
        this.closeDrawer.emit({ selectedDisplay: new SelectListItemModel(), updateValue: false });
    }

    cancelAddDisplay(): void {
        this.confirmAddDisplay = false;
    }

    addDisplay(): void {
        this.confirmAddDisplay = false;
        this.isLoading = true;
        if (this.selectedGroupId && this.selectedGroupId !== -1) {
            this.model.groupId = this.selectedGroupId;
        } else {
            this.model.groupId = null;
        }

        this.model.status = this.displayCanBeAdded || this.priceHidden ? 1 : 3;
        this.model.priceHidden = this.priceHidden;

        this.service.add(this.model)
            .subscribe({
                next: (data: any) => {
                    this.addedDisplayId = data;
                    this.displayId = data;

                    this.isLoading = false;
                    if (this.displayCanBeAdded || this.priceHidden) {
                        this.baseService.success("Screen added successfully.");
                        this.screenActivationTracker.track(this.displayId, this.model.playerType, 'add_screen_drawer');

                        let display = new SelectListItemModel();
                        display.keyInt = this.displayId;
                        display.value = this.model.name;

                        this.closeDrawer.emit({ selectedDisplay: display, updateValue: true });

                    } else {
                        setTimeout(() => {
                            this.buySubscription();
                        });
                    }

                },
                error: (error) => {
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            })
    }


    buySubscription() {
        this.isLoading = true;
        let getDisplayCountRequestSubscription: any;
        let userId = null;
        if (this.model.groupId != null && this.model.groupId !== -1 && !this.isIAmOwner) {
            const groupSummary = this.groups.find(x => x.id === Number(this.model.groupId));
            if (groupSummary) {
                localStorage.setItem(Constants.varGroupSummary, JSON.stringify(groupSummary));
                userId = groupSummary.ownerId;
            } else {
                userId = this.appUtils.getUserId();
            }
            getDisplayCountRequestSubscription = this.service.getDisplayCountByUserOwnership(userId)
                .subscribe({
                    next: (totalCount: number) => {
                        this.isLoading = false;
                        this.totalDisplayCountToSubscribe = totalCount + 1;
                        this.updateSubscription(totalCount);
                    },
                    error: error => {
                        this.isLoading = false;
                        this.baseService.processErrorResponse(error);
                    }
                });
        } else {
            getDisplayCountRequestSubscription = this.service.getDisplayCount(false)
                .subscribe({
                    next: (totalCount: number) => {
                        this.isLoading = false;
                        this.totalDisplayCountToSubscribe = totalCount + 1;
                        this.updateSubscription(totalCount);
                    },
                    error: error => {
                        this.isLoading = false;
                        this.baseService.processErrorResponse(error);
                    }
                });
        }

        this.subscriptions.push(getDisplayCountRequestSubscription);

    }

    updateSubscription(planQuantity: number) {
        this.isLoading = true;
        const model = new UpdateSubscriptionModel();
        if (this.model.groupId != null && this.model.groupId !== -1 && !this.isIAmOwner) {
            model.isUpdatedByGroupUser = true;
        }

        let subscriptionSummary = JSON.parse(localStorage.getItem(Constants.varSubscriptionSummary));

        model.subscriptionId = subscriptionSummary.id;
        // Same rule as display-add (DN-4272): cover every active screen plus the new one, minus the
        // plan's free screens, and never less than one more than already paid for.
        const paid = Number(this.subscriptionSummaryModel?.planQuantity ?? subscriptionSummary.planQuantity) || 0;
        const free = Number(this.subscriptionSummaryModel?.planFreeQuantity ?? subscriptionSummary.planFreeQuantity) || 0;
        model.planQuantity = Math.max(paid + 1, planQuantity + 1 - free);
        this.totalDisplayCountToSubscribe = model.planQuantity;
        model.planId = subscriptionSummary.planId;

        this.subscriptionService.updateSubscription(model)
            .subscribe({
                next: (response: any) => {
                    this.isLoading = false;
                    setTimeout(() => {
                        this.addScreenPurchaseLog();
                    });
                    setTimeout(() => {
                        this.updateDisplayStatus();
                    });
                    this.gtmAnalyticsService.trackPurchase(
                        // DN-4241: Chargebee invoice id from update-subscription — unique per
                        // charge and matched by Chargebee's invoice webhook, so this event
                        // deduplicates against the server-side (Stape) one. The timestamp
                        // fallback is unmatchable and only applies when nothing was billed.
                        response?.invoiceId || `screen_${Date.now()}`,
                        this.subscriptionSummaryModel.planUnitPrice,
                        'USD',
                        [{
                            item_id: this.subscriptionSummaryModel.planId,
                            item_name: this.subscriptionSummaryModel.planName,
                            price: this.subscriptionSummaryModel.planUnitPrice,
                            quantity: 1
                        }]
                    );
                },
                error: error => {
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    updateDisplayStatus() {
        this.isLoading = true;
        const model = new DisplayChangeStatusModel();
        model.id = this.addedDisplayId;
        model.status = 1; // 1: active

        this.service.changeStatus(model)
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    setTimeout(() => {
                        this.refreshToken();
                    });
                },
                error: error => {
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    refreshToken() {
        this.isLoading = true;
        this.userService.getUserInfo()
            .subscribe({
                next: response => {
                    this.isLoading = false;
                    this.appUtils.setUserInformation(response);
                    setTimeout(() => {
                        this.baseService.success("Screen has been added successfully.");
                        this.screenActivationTracker.track(this.displayId, this.model.playerType, 'add_screen_drawer');
                        let display = new SelectListItemModel();
                        display.keyInt = this.displayId;
                        display.value = this.model.name;

                        this.closeDrawer.emit({ selectedDisplay: display, updateValue: true });
                    });
                },
                error: error => {
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    addScreenPurchaseLog() {
        const model = new ScreenPurchaseLogModel();
        model.purchaseType = Constants.subscriptionPurchaseType.update;
        model.quantity = this.totalDisplayCountToSubscribe;
        if (this.model.groupId && this.model.groupId > 0 && !this.isIAmOwner) {
            model.groupId = Number(this.model.groupId);
            const groupSummary = this.groups.find(x => x.id === Number(this.model.groupId));
            model.subscriptionId = groupSummary?.subscriptionId;
        } else {
            const subscriptionSummary = JSON.parse(localStorage.getItem(Constants.varSubscriptionSummary));
            model.groupId = null;
            model.subscriptionId = subscriptionSummary.id;
        }
        this.screenPurchaseLogService.add(model)
            .subscribe({
                next: () => {
                },
                error: (error: any) => {
                    this.baseService.processErrorResponse(error.error);
                }
            });
    }

    ngOnDestroy() {
        this.isLoading = false;
        this.subscriptions.forEach(s => { s.unsubscribe(); });
    }

}
